import { Inject, Injectable } from '@angular/core';
import { Global, GLOBAL } from '../../injection-tokens';
import { BehaviorSubject } from 'rxjs';

const LAST_SELECTED_ENV = 'lastSelectedEnv';
export type Dc = 'il3' | 'eu5' | 'us5' | string;
export type Env = string;
export type Domain = 'gigya.com' | 'cloud.sap';
export const gigyaDomain: Domain = 'gigya.com';
export const cloudSapDomain: Domain = 'cloud.sap';
export const singleBackendEnvs: Env[] = ['prod', 'st1', 'st2', 'st4'];

type DcEnv = { dc: Dc, env: Env, hostEnv: Env };
type DomainDcEnv = { domain: Domain } & DcEnv;

@Injectable({
  providedIn: 'root'
})
export class DcEnvUtilsService {

  private _businessUnitDc$ = new BehaviorSubject<Dc | null>(null);
  private _businessUnitDc: Dc;
  private _dc: Dc;
  private _env: Env;
  private _hostEnv: Env;
  private _domain: Domain;

  public businessUnitDc$ = this._businessUnitDc$.asObservable();

  public get isGigyaDomain(): boolean {
    return this._domain === gigyaDomain;
  }

  public get isCloudSapDomain(): boolean {
    return this._domain === cloudSapDomain;
  }

  public get supportsMultipleBackendEnvs(): boolean {
    return !singleBackendEnvs.includes(this._hostEnv) && this._dc !== 'il3';
  }

  public get isProd(): boolean {
    return this._hostEnv === 'prod';
  }

  constructor(@Inject(GLOBAL) private global: Global) {

    if (!this._getDomain(global.location.hostname)) {
      return;
    }

    this.initService();
  }

  public initService() {
    const hostname = this._removeSAMLPrefix(this.global.location.hostname).toLowerCase();
    const params = this._extractHostData(hostname);
    this._domain = params.domain;
    this._dc = params.dc;
    this._env = params.env;
    this._hostEnv = params.hostEnv;
  }

  public getEnv(): Env {
    return this._dc.startsWith('il') ? `cdp-${this._env}` : this._env;
  }

  public getDomain(): Domain {
    return this._domain;
  }

  public getDc(): Dc {
    if (this._dc.startsWith('il')) {
      return this._dc;
    }

    if (this._businessUnitDc) {
      return this._businessUnitDc;
    }

    return 'eu5';
  }

  public getAPIBaseURL(): string {
    if (this._domain === 'cloud.sap') {
      return `https://api.${this.getDc()}-${this.getEnv()}.cdp.cloud.sap`;
    }
    return `https://cdp.${this.getDc()}-${this.getEnv()}.gigya.com`;
  }

  public setDcByBusinessUnit(dc: Dc): void { // used from store-app
    if (this._dc.startsWith('il')) {
      this._businessUnitDc = this._dc;
      this._businessUnitDc$.next(this._dc);
    } else {
      this._businessUnitDc = dc;
      this._businessUnitDc$.next(dc);
    }
  }

  public setEnv(env: Env): void { // used from universe-app
    this._env = env;
    localStorage.setItem(LAST_SELECTED_ENV, env);
  }

  public getPrimaryApiBaseURL(): string {
    const dcEnvString = `${this._getPrimaryDc()}-${this.getEnv()}`;
    if (this._domain === gigyaDomain) {
      return `https://cdp.${dcEnvString}.${gigyaDomain}/api`;
    }

    if (this._domain === cloudSapDomain) {
      return `https://api.${dcEnvString}.cdp.${cloudSapDomain}/api`;
    }
  }

  private _extractHostData(hostname: string): DomainDcEnv {
    const domain = this._getDomain(hostname);
    const dcEnv = this._getDcEnv(hostname);

    return {...dcEnv, domain};
  }

  private _removeSAMLPrefix(hostname: string): string {
    const samlIdentifier = '.my.';
    if (hostname.includes(samlIdentifier)) {
      return hostname.split(samlIdentifier)[1];
    }

    return hostname;
  }

  private _extractDcEnvFromNonIL(hostname: string): DcEnv {
    const hostEnvRegex = /^(?:([^.]+)\.)?(?:([^.]+)\.)?universe\.cdp\.(gigya\.com|cloud\.sap)$/;
    const match = hostname.match(hostEnvRegex);
    const hostEnv = match?.[1] ?? 'prod';

    if (singleBackendEnvs.includes(hostEnv)) {
      return {dc: 'eu5', env: hostEnv, hostEnv};
    } else {
      return {dc: 'eu5', env: this._getLastEnvFromStorage(), hostEnv};
    }
  }

  private _getDcEnv(hostname: string): DcEnv {
    if (this._isIL(hostname)) {
      return this._extractDcEnvFromIL(hostname);
    } else {
      return this._extractDcEnvFromNonIL(hostname);
    }
  }

  private _isIL(hostname: string): boolean {
    const ilRegex = /il\d+|il\d+-cdp-st\d+/;
    return ilRegex.test(hostname);
  }


  private _extractDcEnvFromIL(hostname: string): DcEnv {
    const specialProdRegex = /\.il\d+\./;
    if (specialProdRegex.test(hostname)) {
      const dc = hostname.split('.').find(seg => seg.startsWith('il'));
      return {dc, env: 'prod', hostEnv: 'prod'};
    }

    const ilRegex = /il\d+-cdp/;
    if (ilRegex.test(hostname)) {
      const dcCdpEnvString = hostname.split('.').find(seg => seg.startsWith('il')); // il3-cdp-st1
      const dcEnvString = dcCdpEnvString.replace('cdp-', ''); // il3-st1
      const [dc, env] = dcEnvString.split('-');
      return {dc, env, hostEnv: env};
    }

  }

  private _getDomain(hostname: string | null): Domain | null {
    if (hostname?.endsWith(gigyaDomain)) {
      return gigyaDomain;
    }

    if (hostname?.endsWith(cloudSapDomain)) {
      return cloudSapDomain;
    }

    return null;
  }

  private _getLastEnvFromStorage(): Env {
    return localStorage.getItem(LAST_SELECTED_ENV) ?? 'st1';
  }

  private _getPrimaryDc(): string {
    return this._dc?.toLowerCase() === 'us5' ? 'eu5' : this._dc;
  }
}
